import { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { format, parseISO, isValid } from "date-fns";
import {
  Car,
  Users,
  Briefcase,
  Route,
  Lock,
  CreditCard,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";
import QuoteSummary from "@/components/QuoteSummary";
import StepProgress from "@/components/StepProgress";
import Honeypot from "@/components/Honeypot";
import { buildQuotePayload } from "@/lib/quote";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function formatDate(iso) {
  if (!iso) return "—";
  const d = parseISO(iso);
  return isValid(d) ? format(d, "EEE, d MMM yyyy") : iso;
}

export default function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Pending booking (built when the customer clicked "Payment Details").
  const pending = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("sjc_pending_booking") || "null");
    } catch (_) {
      return null;
    }
  }, []);

  const summary = useMemo(() => {
    const q = Object.fromEntries(searchParams.entries());
    return {
      service: q.service || pending?.service_type || "—",
      pickup: q.pickup || "—",
      destination: q.destination || "—",
      date: formatDate(q.date),
      time: q.time || "—",
      vehicle: q.vehicle || pending?.vehicle_preference || "—",
      passengers: q.passengers || "—",
      luggage: q.luggage || "—",
    };
  }, [searchParams, pending]);

  // Build the pricing quote payload (prefer full detail from the pending
  // booking; fall back to URL params for pickup/dropoff).
  const quotePayload = useMemo(() => {
    const q = Object.fromEntries(searchParams.entries());
    const details = {
      ...(pending?.details || {}),
      pickup: q.pickup,
      destination: q.destination,
    };
    return buildQuotePayload({
      vehicleName: q.vehicle || pending?.vehicle_preference,
      details,
    });
  }, [searchParams, pending]);

  // Capture the fare shown to the customer so booking emails can display it.
  useEffect(() => {
    if (!quotePayload) return;
    let on = true;
    axios
      .post(`${API}/quote`, quotePayload)
      .then((r) => {
        if (on && r.data?.success) setFareData(r.data);
      })
      .catch(() => {});
    return () => {
      on = false;
    };
  }, [quotePayload]);

  const [customer, setCustomer] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
  });
  const [payment, setPayment] = useState({
    cardName: "",
    cardNumber: "",
    expiry: "",
    cvv: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [hpWebsite, setHpWebsite] = useState(""); // honeypot — stays empty for humans
  const [fareData, setFareData] = useState(null);

  // Scroll to top on mount so the customer lands on the header, not
  // mid-form after coming from the review step.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const updateCustomer = (k, v) => setCustomer((c) => ({ ...c, [k]: v }));
  const updatePayment = (k, v) => setPayment((p) => ({ ...p, [k]: v }));

  // Auto-format card expiry as MM/YY from digits only (handles typing, paste, backspace)
  const formatExpiry = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 4);
    if (digits.length < 3) return digits;
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  };

  const handlePay = async (e) => {
    e.preventDefault();

    // Validate customer info
    if (!customer.firstName.trim() || !customer.lastName.trim()) {
      toast.error("Please enter your first and last name.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!customer.mobile.trim()) {
      toast.error("Please enter your mobile number.");
      return;
    }
    if (payment.expiry && !/^(0[1-9]|1[0-2])\/\d{2}$/.test(payment.expiry)) {
      toast.error("Please enter a valid card expiry (MM/YY).");
      return;
    }

    // Load the pending booking (built when user clicked "Payment Details" on the trip form)
    let pending = null;
    try {
      pending = JSON.parse(sessionStorage.getItem("sjc_pending_booking") || "null");
    } catch (_) {
      pending = null;
    }
    if (!pending) {
      toast.error("Your trip details have expired. Please start again.");
      navigate("/book");
      return;
    }

    // Merge checkout customer info into the payload
    const payload = {
      ...pending,
      name: `${customer.firstName.trim()} ${customer.lastName.trim()}`,
      email: customer.email.trim(),
      phone: customer.mobile.trim(),
      request_type: "booking",
      website: pending.website || hpWebsite,
      details: {
        ...(pending.details || {}),
        ...(fareData
          ? {
              fare: {
                vehicle: fareData.vehicle?.name,
                base_fare: fareData.line_items?.base_fare,
                meet_greet_fee: fareData.line_items?.meet_greet_fee,
                child_seat_charge: fareData.line_items?.child_seat_charge,
                child_seats: fareData.line_items?.child_seats,
                waiting_charge: fareData.line_items?.waiting_charge,
                subtotal: fareData.subtotal,
                gst: fareData.gst,
                total: fareData.total,
                currency: fareData.currency,
              },
            }
          : {}),
      },
    };

    setSubmitting(true);
    try {
      // NOTE: Stripe not connected yet. We submit the booking directly so email confirmations fire.
      const res = await axios.post(`${API}/bookings`, payload);
      sessionStorage.removeItem("sjc_pending_booking");
      toast.success("Booking confirmed! Check your email for confirmation.");
      navigate(`/checkout/success?id=${encodeURIComponent(res.data.id)}`);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or call us at +61 400 000 000.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="min-h-screen bg-ivory">
      <Navbar />

      {/* Midnight editorial header — matches the Booking page header */}
      <section
        data-testid="checkout-header"
        className="bg-midnight text-warm-white pt-32 md:pt-40 pb-16 md:pb-20 border-b border-white/10"
      >
        <div className="max-w-[92rem] mx-auto px-6 lg:px-12">
          <p className="sjc-eyebrow text-gold flex items-center gap-4">
            <span className="sjc-hairline w-10" />
            The Concierge Desk
          </p>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-[72px] leading-[1.02] tracking-[-0.015em] max-w-4xl">
            Complete <span className="italic font-light">your reservation.</span>
          </h1>
          <p className="mt-6 text-warm-white/65 text-base md:text-lg font-light leading-relaxed max-w-xl">
            Review your journey, add your contact details, and confirm securely.
          </p>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-16 md:py-24">
        <div data-testid="checkout-progress" className="mb-12 md:mb-16 max-w-4xl">
          <StepProgress current={4} />
        </div>

        <form onSubmit={handlePay} className="grid lg:grid-cols-5 gap-6 md:gap-8">
          <Honeypot value={hpWebsite} onChange={setHpWebsite} />
          {/* LEFT COLUMN */}
          <aside className="lg:col-span-2 space-y-6">
            <GlassCard testid="summary-card">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <CardTitle icon={Route}>Your Itinerary</CardTitle>
                <span className="text-[10px] tracking-[0.22em] uppercase text-muted-light/70">
                  {summary.service}
                </span>
              </div>

              {/* Pickup -> destination visual */}
              <div className="mt-8 relative">
                <div className="absolute left-[9px] top-6 bottom-6 w-px bg-charcoal/15" aria-hidden />
                <div className="flex items-start gap-4">
                  <span className="w-5 h-5 rounded-full border-2 border-gold mt-0.5 flex-shrink-0" aria-hidden />
                  <div className="min-w-0">
                    <p className="text-[10px] font-medium tracking-[0.22em] uppercase text-muted-light">Pickup</p>
                    <p data-testid="itin-pickup" className="mt-1.5 text-charcoal text-[15px] break-words font-light">{summary.pickup}</p>
                    <p className="mt-1 text-xs text-muted-light italic" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "14px" }}>{summary.date} · {summary.time}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 mt-6">
                  <span className="w-5 h-5 rounded-full bg-gold mt-0.5 flex-shrink-0" aria-hidden />
                  <div className="min-w-0">
                    <p className="text-[10px] font-medium tracking-[0.22em] uppercase text-muted-light">Destination</p>
                    <p data-testid="itin-destination" className="mt-1.5 text-charcoal text-[15px] break-words font-light">{summary.destination}</p>
                  </div>
                </div>
              </div>

              {/* Vehicle + pax + luggage as chips */}
              <div className="mt-8 pt-6 border-t border-charcoal/10 grid grid-cols-3 gap-3">
                <ItinChip icon={Car} label="Vehicle" value={summary.vehicle} testid="itin-vehicle" />
                <ItinChip icon={Users} label="Passengers" value={summary.passengers} testid="itin-passengers" />
                <ItinChip icon={Briefcase} label="Luggage" value={summary.luggage} testid="itin-luggage" />
              </div>
            </GlassCard>

            <GlassCard testid="total-card" accent>
              <QuoteSummary payload={quotePayload} title="Fare Breakdown" />
            </GlassCard>
          </aside>

          {/* RIGHT COLUMN */}
          <div className="lg:col-span-3 space-y-6">            <GlassCard testid="customer-card">
              <CardTitle icon={Users}>Customer Information</CardTitle>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <DarkField label="First Name" required>
                  <DarkInput
                    testid="customer-first-name"
                    value={customer.firstName}
                    onChange={(e) => updateCustomer("firstName", e.target.value)}
                    placeholder="John"
                    autoComplete="given-name"
                  />
                </DarkField>
                <DarkField label="Last Name" required>
                  <DarkInput
                    testid="customer-last-name"
                    value={customer.lastName}
                    onChange={(e) => updateCustomer("lastName", e.target.value)}
                    placeholder="Smith"
                    autoComplete="family-name"
                  />
                </DarkField>
                <DarkField label="Email Address" required>
                  <DarkInput
                    testid="customer-email"
                    type="email"
                    value={customer.email}
                    onChange={(e) => updateCustomer("email", e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </DarkField>
                <DarkField label="Mobile Number" required>
                  <DarkInput
                    testid="customer-mobile"
                    type="tel"
                    value={customer.mobile}
                    onChange={(e) => updateCustomer("mobile", e.target.value)}
                    placeholder="+61 400 123 456"
                    autoComplete="tel"
                  />
                </DarkField>
              </div>
            </GlassCard>

            <GlassCard testid="payment-card">
              <CardTitle icon={CreditCard}>Payment</CardTitle>
              <div className="mt-6 grid grid-cols-1 gap-5">
                <DarkField label="Name on Card">
                  <DarkInput
                    testid="pay-card-name"
                    value={payment.cardName}
                    onChange={(e) => updatePayment("cardName", e.target.value)}
                    placeholder="Name as it appears on card"
                    autoComplete="cc-name"
                  />
                </DarkField>
                <DarkField label="Card Number">
                  <DarkInput
                    testid="pay-card-number"
                    value={payment.cardNumber}
                    onChange={(e) => updatePayment("cardNumber", e.target.value)}
                    placeholder="1234 5678 9012 3456"
                    inputMode="numeric"
                    autoComplete="cc-number"
                  />
                </DarkField>
                <div className="grid grid-cols-2 gap-5">
                  <DarkField label="Expiry Date">
                    <DarkInput
                      testid="pay-expiry"
                      value={payment.expiry}
                      onChange={(e) => updatePayment("expiry", formatExpiry(e.target.value))}
                      placeholder="MM/YY"
                      inputMode="numeric"
                      maxLength={5}
                      autoComplete="cc-exp"
                    />
                  </DarkField>
                  <DarkField label="CVV">
                    <DarkInput
                      testid="pay-cvv"
                      value={payment.cvv}
                      onChange={(e) => updatePayment("cvv", e.target.value)}
                      placeholder="123"
                      inputMode="numeric"
                      autoComplete="cc-csc"
                    />
                  </DarkField>
                </div>
              </div>

              <div
                data-testid="payment-notice"
                className="mt-6 flex items-center gap-2.5 text-[11px] text-muted-light tracking-[0.18em] uppercase font-medium"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-gold" strokeWidth={1.5} />
                <span>Secured by Stripe · 256-bit encryption</span>
              </div>

              <Button
                type="submit"
                data-testid="pay-securely-btn"
                disabled={submitting}
                className="mt-8 w-full bg-midnight hover:bg-gold text-warm-white hover:text-midnight font-semibold tracking-[0.28em] uppercase text-[11px] py-6 rounded-none transition-all duration-500 disabled:opacity-70"
              >
                <Lock className="w-4 h-4 mr-2" />
                {submitting ? "Confirming..." : "Pay Securely"}
              </Button>
            </GlassCard>
          </div>
        </form>
      </main>

      <Footer />
      <FloatingActions />
    </div>
  );
}

/* ---------- Reusable pieces ---------- */

function GlassCard({ children, testid, accent = false }) {
  return (
    <section
      data-testid={testid}
      className={`bg-white p-7 md:p-9 border rounded-none ${
        accent ? "border-gold/30" : "border-charcoal/10"
      }`}
    >
      {children}
    </section>
  );
}

function CardTitle({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-3.5">
      {Icon && (
        <span className="w-9 h-9 border border-charcoal/15 grid place-items-center text-gold">
          <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
        </span>
      )}
      <h2 className="font-display text-charcoal text-xl md:text-2xl leading-tight tracking-tight">
        {children}
      </h2>
    </div>
  );
}


function ItinChip({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="border border-charcoal/10 bg-ivory px-3.5 py-3.5 min-w-0 overflow-hidden">
      <div className="flex items-center gap-1.5 text-[9px] sm:text-[10px] tracking-[0.22em] uppercase text-muted-light truncate font-medium">
        <Icon className="w-3 h-3 flex-shrink-0 text-gold" strokeWidth={1.5} />
        <span className="truncate">{label}</span>
      </div>
      <p className="mt-2 text-sm text-charcoal break-words leading-tight font-light">{value}</p>
    </div>
  );
}

function DarkField({ label, required, children }) {
  return (
    <div>
      <Label className="text-[11px] tracking-[0.22em] uppercase font-medium text-muted-light inline-flex items-center gap-1">
        {label}
        {required && <span className="text-gold text-sm leading-none">*</span>}
      </Label>
      <div className="mt-2.5">{children}</div>
    </div>
  );
}

function DarkInput({ testid, ...props }) {
  return (
    <Input
      data-testid={testid}
      {...props}
      className="border-charcoal/15 rounded-none h-11 focus-visible:border-midnight focus-visible:ring-0 bg-white text-sm text-charcoal placeholder:text-zinc-400"
    />
  );
}
